/**
 * Async utilities for improving reliability of asynchronous operations
 * This module provides Promise-based utilities to replace setTimeout and
 * improve async coordination throughout the application.
 */

/**
 * Creates a Promise that resolves after the specified delay
 * @param {number} ms - Delay in milliseconds
 * @returns {Promise} - Promise that resolves after the delay
 */
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Creates a Promise with timeout capability
 * @param {Promise} promise - The Promise to wrap with a timeout
 * @param {number} timeoutMs - Timeout in milliseconds
 * @param {string} errorMessage - Optional custom error message
 * @returns {Promise} - Promise that rejects if the timeout is reached before the original Promise resolves
 */
function withTimeout(promise, timeoutMs, errorMessage = 'Operation timed out') {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error(errorMessage));
    }, timeoutMs);

    promise
      .then(result => {
        clearTimeout(timeoutId);
        resolve(result);
      })
      .catch(error => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });
}

/**
 * Creates a cancellable Promise
 * @returns {Object} Object with promise and cancel function
 */
function cancellable() {
  let rejectFn;
  const promise = new Promise((resolve, reject) => {
    rejectFn = reject;
  });
  
  const cancel = () => {
    const error = new Error('Operation cancelled');
    error.isCancellation = true;
    rejectFn(error);
  };
  
  return {
    promise,
    cancel
  };
}

/**
 * Enhanced fetch with timeout, error handling, and automatic JSON parsing
 * @param {string} url - URL to fetch
 * @param {Object} options - Fetch options
 * @param {number} timeoutMs - Timeout in milliseconds (default: 10000)
 * @returns {Promise} - Promise resolving to response data
 */
async function fetchWithTimeout(url, options = {}, timeoutMs = 10000) {
  // Create abort controller for timeout
  const controller = new AbortController();
  
  // Handle existing signal if provided in options
  const { signal, ...fetchOptions } = options;
  
  // Combine with existing abort signal if one was provided
  const combinedOptions = {
    ...fetchOptions,
    signal: controller.signal
  };
  
  // Set up the timeout
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  
  try {
    const response = await fetch(url, combinedOptions);
    
    clearTimeout(timeoutId);
    
    // Handle non-ok responses
    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(errorText || response.statusText || 'Network response was not ok');
      error.status = response.status;
      error.statusText = response.statusText;
      throw error;
    }
    
    // Check content type for JSON
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }
    
    return await response.text();
  } catch (error) {
    clearTimeout(timeoutId);
    
    // Enhance error with additional information
    if (error.name === 'AbortError') {
      throw new Error(`Request timed out after ${timeoutMs}ms`);
    }
    
    throw error;
  }
}

/**
 * Creates a retry function with exponential backoff
 * @param {Function} fn - Async function to retry
 * @param {Object} options - Retry options
 * @param {number} options.maxRetries - Maximum number of retries (default: 3)
 * @param {number} options.baseDelay - Base delay in ms (default: 300)
 * @param {number} options.maxDelay - Maximum delay in ms (default: 5000)
 * @returns {Function} - Function that will retry the original function
 */
function withRetry(fn, options = {}) {
  const {
    maxRetries = 3,
    baseDelay = 300,
    maxDelay = 5000
  } = options;
  
  return async function(...args) {
    let lastError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fn(...args);
      } catch (error) {
        lastError = error;
        
        // If this was the last attempt, don't delay
        if (attempt === maxRetries) {
          break;
        }
        
        // Calculate delay with exponential backoff and jitter
        const delay = Math.min(
          Math.ceil(baseDelay * Math.pow(2, attempt) * (0.8 + Math.random() * 0.4)),
          maxDelay
        );
        
        // Log retry attempt
        console.log(`Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms`, { error: error.message });
        
        // Wait before next attempt
        await sleep(delay);
      }
    }
    
    // If we got here, all attempts failed
    throw lastError;
  };
}

/**
 * Creates a debounced function that returns a Promise resolving with the 
 * result of the original function's most recent invocation
 * @param {Function} fn - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function returning a Promise
 */
function debounce(fn, wait) {
  let timeout;
  let resolver = null;
  let rejector = null;
  let pendingPromise = null;
  
  return function(...args) {
    // If no pending promise, create a new one
    if (!pendingPromise) {
      pendingPromise = new Promise((resolve, reject) => {
        resolver = resolve;
        rejector = reject;
      });
    }
    
    // Clear previous timeout
    if (timeout) {
      clearTimeout(timeout);
    }
    
    // Set new timeout
    timeout = setTimeout(() => {
      try {
        const result = fn.apply(this, args);
        resolver(result);
      } catch (error) {
        rejector(error);
      } finally {
        // Reset for next invocation
        timeout = null;
        resolver = null;
        rejector = null;
        pendingPromise = null;
      }
    }, wait);
    
    return pendingPromise;
  };
}

/**
 * Creates a promise that resolves when a CSS transition completes
 * @param {HTMLElement} element - Element to watch for transition
 * @param {Function} setupFn - Function that initiates the transition
 * @param {number} timeoutMs - Timeout in milliseconds (default: 1000)
 * @returns {Promise} - Promise resolving when transition ends
 */
function transitionPromise(element, setupFn, timeoutMs = 1000) {
  return new Promise((resolve, reject) => {
    if (!element) {
      reject(new Error('No element provided for transition'));
      return;
    }
    
    // Set up transition end handler
    const handleTransitionEnd = (event) => {
      if (event.target === element) {
        cleanup();
        resolve();
      }
    };
    
    // Set up timeout for fallback
    const timeoutId = setTimeout(() => {
      cleanup();
      resolve(); // Resolve anyway to prevent blocking
    }, timeoutMs);
    
    // Cleanup function
    const cleanup = () => {
      element.removeEventListener('transitionend', handleTransitionEnd);
      clearTimeout(timeoutId);
    };
    
    // Set up event listener
    element.addEventListener('transitionend', handleTransitionEnd);
    
    // Run setup function to initiate transition
    try {
      setupFn(element);
    } catch (error) {
      cleanup();
      reject(error);
    }
  });
}

/**
 * Creates a promise that resolves on the next animation frame
 * @returns {Promise} - Promise resolving on next animation frame
 */
function nextFrame() {
  return new Promise(resolve => requestAnimationFrame(resolve));
}

/**
 * Creates a promise that resolves when an animation completes
 * @param {HTMLElement} element - Element to animate
 * @param {Array|Object} keyframes - Keyframes for the animation
 * @param {Object} options - Animation options
 * @returns {Promise} - Promise resolving with the Animation
 */
function animatePromise(element, keyframes, options) {
  return new Promise((resolve, reject) => {
    try {
      const animation = element.animate(keyframes, options);
      animation.onfinish = () => resolve(animation);
      animation.oncancel = () => reject(new Error('Animation cancelled'));
      animation.onerror = (error) => reject(error || new Error('Animation error'));
    } catch (error) {
      reject(error);
    }
  });
}

// Export utilities
window.asyncUtils = {
  sleep,
  withTimeout,
  cancellable,
  fetchWithTimeout,
  withRetry,
  debounce,
  transitionPromise,
  nextFrame,
  animatePromise
};